import { PantallaCargando } from '@/components/Cargando';
import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import SectionHelp from '@/components/help/SectionHelp';
// `Package` se usa en el aviso del vehículo del taller y llevaba tiempo sin
// importar: la venta se abría bien y reventaba en cuanto el lead traía vehículo.
// Lo encontró la prueba nueva de identificadores, no el build.
import { Wallet, ShoppingCart, Send, History, Trash2, Lock, Unlock, Printer, Settings, CreditCard, Loader2, UserPlus, Package } from 'lucide-react';

const STATUS_LABELS = { pending: 'Pendiente', paid: 'Pagada', cancelled: 'Cancelada', refunded: 'Reembolsada' };
const STATUS_COLORS = {
  pending: 'bg-amber-100 text-amber-800', paid: 'bg-emerald-100 text-emerald-800',
  cancelled: 'bg-slate-100 text-slate-600', refunded: 'bg-red-100 text-red-800',
};

function money(v, currency = 'MXN') {
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: currency || 'MXN' }).format(v || 0);
}

export default function PosHub() {
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [session, setSession] = useState(null);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openingAmount, setOpeningAmount] = useState('');
  const [closingCounted, setClosingCounted] = useState('');
  const [closingResult, setClosingResult] = useState(null);
  const [cart, setCart] = useState([]);
  const [saleType, setSaleType] = useState('cash');
  const [leadSearch, setLeadSearch] = useState('');
  const [leadOptions, setLeadOptions] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [crmDealId, setCrmDealId] = useState('');
  const [dealVehicle, setDealVehicle] = useState(null);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [lastRemoteResult, setLastRemoteResult] = useState(null);
  const [printerConfig, setPrinterConfig] = useState(null);
  const [showPrinterSettings, setShowPrinterSettings] = useState(false);
  const [printerForm, setPrinterForm] = useState({ name: 'Impresora de tickets', connection_type: 'browser', printer_ip: '', printer_port: 9100, paper_width_mm: 80, enabled: true, station: 'caja' });
  // S135 — todas las estaciones del negocio, no solo la de caja.
  const [estaciones, setEstaciones] = useState([]);
  const [savingPrinter, setSavingPrinter] = useState(false);
  const [printingId, setPrintingId] = useState(null);
  const [amountTendered, setAmountTendered] = useState('');
  const [lastChangeResult, setLastChangeResult] = useState(null); // { amount_tendered, change_due } from the last cash sale
  const [terminalConfig, setTerminalConfig] = useState(null);
  // La terminal bancaria viene desde el paquete Pro. Si el plan no la incluye,
  // el backend responde 403 y aquí NO es un error que mostrar: se oculta la
  // sección y punto (antes ese 403 tumbaba toda la carga del POS con un toast).
  const [terminalProGated, setTerminalProGated] = useState(false);
  const [showTerminalSettings, setShowTerminalSettings] = useState(false);
  const [terminalForm, setTerminalForm] = useState({ device_id: '', enabled: true, provider_key: 'mercadopago' });
  const [savingTerminal, setSavingTerminal] = useState(false);
  const [terminalWaiting, setTerminalWaiting] = useState(null); // sale_id being polled, or null
  // S55 (1/N) — POS operativo: IVA por tenant, descuento de venta, pago dividido
  const [posConfig, setPosConfig] = useState(null);
  const [showPosConfig, setShowPosConfig] = useState(false);
  const [posConfigForm, setPosConfigForm] = useState({ tax_rate: 16, prices_include_tax: true });
  const [savingPosConfig, setSavingPosConfig] = useState(false);
  const [discountType, setDiscountType] = useState('none');
  const [discountValue, setDiscountValue] = useState('');
  const [splitPayment, setSplitPayment] = useState(false);
  const [paymentLines, setPaymentLines] = useState([{ payment_method: 'cash', amount: '' }]);
  const [refundingId, setRefundingId] = useState(null);
  // S56 (1/N) — membresías del cliente elegido (descuento/créditos VIP) +
  // modal de PIN de supervisor para reembolsos/descuentos grandes de un cajero.
  const [memberships, setMemberships] = useState([]);
  const [pinModal, setPinModal] = useState(null); // { message, onConfirm(pin) } | null
  const [pinInput, setPinInput] = useState('');
  const [pinSubmitting, setPinSubmitting] = useState(false);
  // S57 (2/N) — "cliente nuevo" (venta mostrador): crear el lead con solo
  // el nombre, sin salir del Punto de Venta, en vez de obligar a ir al CRM
  // primero. También lee ?crm_lead_id= al entrar desde "Crear venta" en la
  // ficha del lead.
  const [showNewLeadForm, setShowNewLeadForm] = useState(false);
  const [newLeadName, setNewLeadName] = useState('');
  const [creatingLead, setCreatingLead] = useState(false);
  const [leadFromUrlLoading, setLeadFromUrlLoading] = useState(false);

  const loadAll = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get('/tenant/catalog/items').then(r => setItems((r.data || []).filter(i => i.status !== 'inactive'))),
      api.get('/tenant/pos/cash-sessions/current').then(r => setSession(r.data)),
      api.get('/tenant/pos/sales').then(r => setSales(r.data || [])),
      api.get('/tenant/pos/printer-configs')
        .then(r => setEstaciones(r.data?.estaciones || []))
        .catch(() => setEstaciones([])),
      api.get('/tenant/pos/printer-config').then(r => {
        setPrinterConfig(r.data);
        if (r.data) setPrinterForm({ ...r.data, printer_ip: r.data.printer_ip || '' });
      }).catch(() => setPrinterConfig(null)),
      api.get('/tenant/pos/terminal-config').then(r => {
        setTerminalConfig(r.data);
        setTerminalProGated(false);
        if (r.data) setTerminalForm({ device_id: r.data.device_id || '', enabled: r.data.enabled, provider_key: r.data.provider_key || 'mercadopago' });
      }).catch(e => {
        // 403 = el plan no incluye la terminal (viene con Pro): se oculta, no se
        // muestra error. Cualquier otro fallo también degrada sin tumbar el POS.
        setTerminalConfig(null);
        if (e.response?.status === 403) setTerminalProGated(true);
      }),
      api.get('/tenant/pos/config').then(r => { setPosConfig(r.data); setPosConfigForm(r.data); }),
    ]).catch(e => toast.error(formatApiError(e.response?.data?.detail))).finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    // S57 (2/N) — llegamos aquí desde "Crear venta" en la ficha del lead
    // (CrmLeads.js) con ?crm_lead_id= — precargamos ese cliente una sola
    // vez al montar, sin pisar una selección manual posterior.
    const leadId = searchParams.get('crm_lead_id');
    if (!leadId) return;
    setLeadFromUrlLoading(true);
    api.get(`/tenant/crm/leads/${leadId}`)
      .then(r => setSelectedLead(r.data))
      .catch(() => toast.error('No se pudo cargar el cliente desde el enlace'))
      .finally(() => setLeadFromUrlLoading(false));
    // 2026-07-24 — cierre del gap de POS para la vertical Talleres: si la
    // venta se abrió desde una orden de reparación (CrmLeads.js manda
    // &crm_deal_id= cuando el lead tiene un deal), traemos su vehículo
    // (WorkshopServiceVehicle, ya embebido en GET /crm/deals/{id}) para
    // mostrarlo y para que la venta quede ligada a esa orden específica,
    // no solo al cliente en general.
    const dealId = searchParams.get('crm_deal_id');
    if (dealId) {
      setCrmDealId(dealId);
      api.get(`/tenant/crm/deals/${dealId}`)
        .then(r => setDealVehicle(r.data?.vehicle || null))
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // S56 (1/N) — el buscador de cliente ahora aplica a cualquier tipo de
    // venta (antes solo a "Link por WhatsApp"), para poder ligar una venta
    // en efectivo a un cliente real y así aplicar sus beneficios de
    // membresía — no solo cuando se manda un link de pago.
    if (!leadSearch) { setLeadOptions([]); return; }
    const t = setTimeout(() => {
      api.get('/tenant/crm/leads', { params: { search: leadSearch, page_size: 10 } })
        .then(r => setLeadOptions(r.data?.items || []))
        .catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [leadSearch]);

  useEffect(() => {
    if (!selectedLead) { setMemberships([]); return; }
    api.get(`/tenant/pos/customer-membership/${selectedLead.id}`)
      .then(r => setMemberships(r.data?.memberships || []))
      .catch(() => setMemberships([]));
  }, [selectedLead]);

  const addToCart = (item) => {
    setCart(prev => {
      const existing = prev.find(l => l.catalog_item_id === item.id);
      if (existing) return prev.map(l => l.catalog_item_id === item.id ? { ...l, quantity: l.quantity + 1 } : l);
      return [...prev, {
        catalog_item_id: item.id, name: item.name, quantity: 1, price_min: item.price_min || 0,
        category: item.pos_category || null, membership_id: null, use_membership_credit: false,
      }];
    });
  };
  const updateQty = (id, qty) => setCart(prev => prev.map(l => l.catalog_item_id === id ? { ...l, quantity: Math.max(1, qty) } : l));
  const removeFromCart = (id) => setCart(prev => prev.filter(l => l.catalog_item_id !== id));
  const cartTotal = cart.reduce((sum, l) => sum + (l.use_membership_credit ? 0 : (l.price_min || 0) * l.quantity), 0);

  // S57 (2/N) — venta mostrador con cliente nuevo: crea el lead con solo el
  // nombre desde la propia Caja (queda en el CRM como cualquier otro lead,
  // solo que capturado desde el punto de venta en vez del formulario de CRM).
  const createNewLead = async () => {
    if (!newLeadName.trim()) return toast.error('Escribe el nombre del cliente');
    setCreatingLead(true);
    try {
      const res = await api.post('/tenant/crm/leads', { display_name: newLeadName.trim() });
      setSelectedLead(res.data);
      setShowNewLeadForm(false);
      setNewLeadName('');
      toast.success('Cliente creado');
    } catch {
      toast.error('No se pudo crear el cliente');
    } finally {
      setCreatingLead(false);
    }
  };

  // S55 (1/N) — mismo cálculo que create_sale() en el backend: descuento de
  // venta sobre el subtotal, luego IVA extraído o sumado según config del
  // tenant. Solo es una vista previa — el backend recalcula y es lo que
  // realmente se guarda.
  const discountAmount = discountType === 'none' || !discountValue
    ? 0
    : discountType === 'percentage'
      ? Math.min(cartTotal * (Number(discountValue) || 0) / 100, cartTotal)
      : Math.min(Number(discountValue) || 0, cartTotal);
  const taxableBase = cartTotal - discountAmount;
  const taxRate = posConfig?.tax_rate ?? 16;
  const pricesIncludeTax = posConfig?.prices_include_tax ?? true;
  const taxAmount = pricesIncludeTax ? taxableBase - (taxableBase / (1 + taxRate / 100)) : taxableBase * taxRate / 100;
  const grandTotal = pricesIncludeTax ? taxableBase : taxableBase + taxAmount;

  const paymentLinesSum = paymentLines.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
  const paymentLinesMatch = Math.abs(paymentLinesSum - grandTotal) < 0.01;

  const openCaja = async () => {
    try {
      await api.post('/tenant/pos/cash-sessions/open', { opening_amount: Number(openingAmount) || 0 });
      toast.success('Caja abierta');
      setOpeningAmount('');
      setClosingResult(null);
      loadAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const closeCaja = async () => {
    if (!session) return;
    try {
      const r = await api.post(`/tenant/pos/cash-sessions/${session.id}/close`, { closing_amount_counted: Number(closingCounted) || 0 });
      setClosingResult(r.data);
      toast.success('Caja cerrada');
      setClosingCounted('');
      loadAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // Sprint 53 (2/N) — no existía forma de descargar/volver a ver el arqueo
  // de una caja ya cerrada; se arma un CSV en el navegador a partir del
  // reporte real (desglose por método de pago + listado de ventas).
  const downloadArqueo = async () => {
    if (!closingResult?.id) return;
    try {
      const r = await api.get(`/tenant/pos/cash-sessions/${closingResult.id}/report`);
      const rep = r.data;
      const lines = [
        'Arqueo de caja',
        `Abierta: ${rep.session.opened_at || ''}`,
        `Cerrada: ${rep.session.closed_at || ''}`,
        `Monto inicial: ${money(rep.session.opening_amount)}`,
        `Efectivo esperado: ${money(rep.session.closing_amount_expected)}`,
        `Efectivo contado: ${money(rep.session.closing_amount_counted)}`,
        `Diferencia: ${money(rep.session.difference)}`,
        '',
        'Desglose por método de pago',
        ...Object.entries(rep.by_payment_method).map(([k, v]) => `${k},${v}`),
        '',
        'Desglose por cajero',
        ...(rep.by_cashier || []).map(c => `${c.name},${c.sale_count} ventas,${c.total}`),
        '',
        'Desglose por categoría',
        ...Object.entries(rep.by_category || {}).map(([k, v]) => `${k},${v}`),
        '',
        'Fecha,Método,Total,Descripción',
        ...rep.sales.map(s => `${s.created_at || ''},${s.payment_method || ''},${s.total},"${(s.notes || '').replace(/"/g, "'")}"`),
      ];
      const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `arqueo-${closingResult.id.slice(0, 8)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const submitCashSale = async (supervisorPin) => {
    if (!cart.length) return toast.error('Agrega al menos un producto o servicio');
    if (!session) return toast.error('Abre la caja antes de registrar una venta en efectivo');
    if (splitPayment && !paymentLinesMatch) return toast.error('La suma de los pagos debe coincidir con el total');
    setSubmitting(true);
    try {
      const result = await api.post('/tenant/pos/sales/cash', {
        items: cart.map(l => ({
          catalog_item_id: l.catalog_item_id, quantity: l.quantity,
          membership_id: l.use_membership_credit ? l.membership_id : undefined,
          use_membership_credit: !!l.use_membership_credit,
        })),
        crm_lead_id: selectedLead?.id || undefined,
        crm_deal_id: crmDealId || undefined,
        notes: notes || undefined,
        amount_tendered: (!splitPayment && amountTendered !== '') ? Number(amountTendered) : undefined,
        discount_type: discountType,
        discount_value: discountType !== 'none' && discountValue !== '' ? Number(discountValue) : undefined,
        payments: splitPayment ? paymentLines.map(p => ({ payment_method: p.payment_method, amount: Number(p.amount) || 0 })) : undefined,
        supervisor_pin: supervisorPin || undefined,
      });
      toast.success('Venta registrada y cobrada');
      if (result.data.change_due != null) {
        setLastChangeResult({ amount_tendered: result.data.amount_tendered, change_due: result.data.change_due });
      } else {
        setLastChangeResult(null);
      }
      setCart([]); setNotes(''); setLastRemoteResult(null); setAmountTendered('');
      setDiscountType('none'); setDiscountValue(''); setSplitPayment(false); setPaymentLines([{ payment_method: 'cash', amount: '' }]);
      setPinModal(null); setPinInput('');
      loadAll();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.code === 'supervisor_pin_required') {
        setPinModal({ message: detail.detail, onConfirm: (pin) => submitCashSale(pin) });
      } else {
        toast.error(formatApiError(detail));
      }
    } finally { setSubmitting(false); }
  };

  const savePosConfig = async () => {
    setSavingPosConfig(true);
    try {
      const r = await api.put('/tenant/pos/config', {
        tax_rate: Number(posConfigForm.tax_rate) || 0,
        prices_include_tax: posConfigForm.prices_include_tax,
      });
      setPosConfig(r.data);
      toast.success('Configuración de IVA guardada');
      setShowPosConfig(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSavingPosConfig(false); }
  };

  const refundSale = async (id, supervisorPin) => {
    if (!supervisorPin && !window.confirm('¿Reembolsar esta venta? Se repondrá el inventario y quedará marcada como reembolsada. Si se pagó con tarjeta o link, el dinero no se regresa solo — deberás procesarlo manualmente en el panel del proveedor.')) return;
    setRefundingId(id);
    try {
      const r = await api.post(`/tenant/pos/sales/${id}/refund`, { supervisor_pin: supervisorPin || undefined });
      if (r.data.needs_manual_provider_refund) {
        toast.success('Venta reembolsada — recuerda regresar el dinero manualmente en el panel de tu proveedor de pagos');
      } else {
        toast.success('Venta reembolsada');
      }
      setPinModal(null); setPinInput('');
      loadAll();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.code === 'supervisor_pin_required') {
        setPinModal({ message: detail.detail, onConfirm: (pin) => refundSale(id, pin) });
      } else {
        toast.error(formatApiError(detail));
      }
    } finally { setRefundingId(null); }
  };

  const submitRemoteSale = async () => {
    if (!cart.length) return toast.error('Agrega al menos un producto o servicio');
    if (!selectedLead) return toast.error('Elige un cliente del CRM para enviarle el link de pago');
    setSubmitting(true);
    try {
      const r = await api.post('/tenant/pos/sales/remote', {
        items: cart.map(l => ({ catalog_item_id: l.catalog_item_id, quantity: l.quantity })),
        crm_lead_id: selectedLead.id, crm_deal_id: crmDealId || undefined, notes: notes || undefined,
      });
      setLastRemoteResult(r.data);
      if (r.data.whatsapp_sent) toast.success('Link de pago enviado por WhatsApp');
      else toast.error('El link se generó pero no se pudo enviar por WhatsApp — cópialo y mándalo a mano');
      setCart([]); setNotes(''); setSelectedLead(null); setLeadSearch(''); setCrmDealId(''); setDealVehicle(null);
      loadAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSubmitting(false); }
  };

  const cancelSale = async (id) => {
    try {
      await api.post(`/tenant/pos/sales/${id}/cancel`);
      toast.success('Venta cancelada');
      loadAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveTerminalConfig = async () => {
    if (terminalForm.provider_key === 'mercadopago' && !terminalForm.device_id.trim()) {
      return toast.error('Ingresa el ID de la terminal (device_id) de Mercado Pago Point');
    }
    setSavingTerminal(true);
    try {
      const r = await api.post('/tenant/pos/terminal-config', terminalForm);
      setTerminalConfig(r.data);
      toast.success('Terminal guardada');
      setShowTerminalSettings(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSavingTerminal(false); }
  };

  const submitTerminalSale = async () => {
    if (!cart.length) return toast.error('Agrega al menos un producto o servicio');
    setSubmitting(true);
    try {
      const r = await api.post('/tenant/pos/sales/terminal', {
        items: cart.map(l => ({ catalog_item_id: l.catalog_item_id, quantity: l.quantity })),
        notes: notes || undefined,
      });
      toast.success('Cobro enviado a la terminal — esperando al cliente');
      setCart([]); setNotes('');
      setTerminalWaiting(r.data.id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSubmitting(false); }
  };

  const cancelTerminalWait = async () => {
    const saleId = terminalWaiting;
    setTerminalWaiting(null);
    if (saleId) {
      try { await api.post(`/tenant/pos/sales/${saleId}/cancel`); } catch { /* leave pending, visible in history */ }
    }
    loadAll();
  };

  // S48 (10/N) — poll the terminal every 2.5s while waiting; the cashier's
  // screen has no other way to know when the customer actually taps/
  // inserts their card on the physical device.
  useEffect(() => {
    if (!terminalWaiting) return undefined;
    const interval = setInterval(async () => {
      try {
        const r = await api.get(`/tenant/pos/sales/${terminalWaiting}/terminal-status`);
        if (r.data.state === 'approved') {
          toast.success('¡Pago aprobado en la terminal!');
          setTerminalWaiting(null);
          loadAll();
        } else if (r.data.state === 'rejected' || r.data.state === 'canceled') {
          toast.error('El cobro en la terminal fue rechazado o cancelado');
          setTerminalWaiting(null);
          loadAll();
        }
      } catch { /* transient network hiccup — keep polling */ }
    }, 2500);
    return () => clearInterval(interval);
  }, [terminalWaiting, loadAll]);

  const savePrinterConfig = async () => {
    setSavingPrinter(true);
    try {
      const r = await api.post('/tenant/pos/printer-config', {
        ...printerForm,
        printer_port: Number(printerForm.printer_port) || 9100,
        paper_width_mm: Number(printerForm.paper_width_mm) || 80,
      });
      setPrinterConfig(r.data);
      toast.success(`Impresora de ${printerForm.station} guardada`);
      api.get('/tenant/pos/printer-configs')
        .then(r => setEstaciones(r.data?.estaciones || []))
        .catch(() => {});
      setShowPrinterSettings(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSavingPrinter(false); }
  };

  const openBrowserTicket = (html) => {
    const win = window.open('', '_blank', 'width=400,height=600');
    if (!win) { toast.error('El navegador bloqueó la ventana de impresión — permite pop-ups para este sitio'); return; }
    win.document.write(html);
    win.document.close();
  };

  const printSale = async (id) => {
    setPrintingId(id);
    try {
      const r = await api.post(`/tenant/pos/sales/${id}/print`);
      if (r.data.mode === 'network') {
        if (r.data.ok) toast.success('Ticket enviado a la impresora');
        else {
          toast.error(formatApiError(r.data.error) || 'No se pudo imprimir en la impresora de red');
          if (r.data.html) openBrowserTicket(r.data.html);
        }
      } else {
        openBrowserTicket(r.data.html);
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setPrintingId(null); }
  };

  if (loading) return <PantallaCargando />;

  return (
    <div className="space-y-6" data-testid="pos-hub">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#0F172A] tracking-tight flex items-center gap-2">
            <Wallet className="w-6 h-6" /> Punto de venta
          </h1>
          <p className="text-sm text-[#64748B] mt-1">Cobra en efectivo o manda un link de pago real por WhatsApp</p>
        </div>
        <div className="text-right flex flex-col items-end gap-2">
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowPosConfig(v => !v)} className="flex items-center gap-1.5 whitespace-nowrap">
              <Settings className="w-4 h-4" /> IVA
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowPrinterSettings(v => !v)} className="flex items-center gap-1.5 whitespace-nowrap">
              <Settings className="w-4 h-4" /> Impresora de tickets
            </Button>
            {!terminalProGated && (
              <Button variant="outline" size="sm" onClick={() => setShowTerminalSettings(v => !v)} className="flex items-center gap-1.5 whitespace-nowrap">
                <CreditCard className="w-4 h-4" /> Terminal bancaria
              </Button>
            )}
          </div>
          {posConfig && (
            <p className="text-[11px] text-[#94A3B8]">
              IVA {posConfig.tax_rate}% · precios {posConfig.prices_include_tax ? 'con IVA incluido' : 'más IVA'}
            </p>
          )}
          {printerConfig && (
            <p className="text-[11px] text-[#94A3B8]">
              {printerConfig.connection_type === 'network' ? `Red: ${printerConfig.printer_ip}` : 'Navegador'} · {printerConfig.paper_width_mm}mm
            </p>
          )}
          {terminalConfig && (
            <p className="text-[11px] text-[#94A3B8]">
              {terminalConfig.provider_key === 'clip' ? 'Clip' : 'Mercado Pago Point'}{terminalConfig.device_id ? ` · ${terminalConfig.device_id}` : ''} · {terminalConfig.enabled ? 'activa' : 'desactivada'}
            </p>
          )}
        </div>
      </div>

      {showPosConfig && (
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-4" data-testid="pos-config-panel">
          <h2 className="text-sm font-semibold text-[#0F172A] mb-1 flex items-center gap-2"><Settings className="w-4 h-4" /> IVA del punto de venta</h2>
          <p className="text-xs text-[#64748B] mb-3">
            Se aplica a toda venta nueva. Cambiarlo no modifica ventas ya hechas — cada venta guarda el IVA con el que se cobró.
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-[#64748B] mb-1">Tasa de IVA (%)</label>
              <input type="number" min="0" max="100" step="0.01" value={posConfigForm.tax_rate}
                onChange={e => setPosConfigForm(f => ({ ...f, tax_rate: e.target.value }))}
                className="border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm w-full" />
            </div>
            <div>
              <label className="block text-xs text-[#64748B] mb-1">¿Tus precios ya incluyen el IVA?</label>
              <select value={posConfigForm.prices_include_tax ? 'yes' : 'no'}
                onChange={e => setPosConfigForm(f => ({ ...f, prices_include_tax: e.target.value === 'yes' }))}
                className="border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm w-full">
                <option value="yes">Sí, el precio del catálogo ya trae IVA</option>
                <option value="no">No, hay que sumarlo aparte</option>
              </select>
            </div>
          </div>
          <div className="mt-3">
            <Button onClick={savePosConfig} disabled={savingPosConfig}>Guardar IVA</Button>
          </div>
        </div>
      )}

      {showPrinterSettings && (
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-4" data-testid="pos-printer-settings">
          <h2 className="text-sm font-semibold text-[#0F172A] mb-1 flex items-center gap-2"><Printer className="w-4 h-4" /> Impresora de tickets</h2>
          <p className="text-xs text-[#64748B] mb-3">
            Funciona con impresoras térmicas de red (Epson, Star, y clones genéricos ESC/POS — el estándar es el mismo para casi cualquier marca).
            Si tu impresora se conecta por USB, elige "Imprimir desde el navegador" — se abrirá una ventana lista para imprimir cada vez.
          </p>
          <div className="mb-3 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0] p-3">
            <label className="block text-xs font-medium text-[#0F172A] mb-1">¿Dónde está esta impresora?</label>
            <select value={printerForm.station}
              onChange={e => {
                const est = e.target.value;
                const ya = estaciones.find(x => x.station === est);
                // Se cargan los datos que YA tenía esa estación, si los tenía.
                // Sin esto, cambiar de estación dejaba el formulario con la IP
                // de la anterior y se guardaba la impresora equivocada.
                setPrinterForm(f => ({
                  ...f, station: est,
                  name: ya?.name || `Impresora de ${est}`,
                  connection_type: ya?.connection_type || 'browser',
                  printer_ip: ya?.printer_ip || '',
                  printer_port: ya?.printer_port || 9100,
                  paper_width_mm: ya?.paper_width_mm || 80,
                  enabled: ya?.enabled !== false,
                }));
              }}
              className="border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm w-full bg-white">
              {(estaciones.length ? estaciones : [{ station: 'caja', etiqueta: 'Caja — la cuenta del cliente' }]).map(e => (
                <option key={e.station} value={e.station}>
                  {e.etiqueta}{e.configurada ? ' · ya configurada' : ''}
                </option>
              ))}
            </select>
            <p className="text-[11px] text-[#64748B] mt-1.5">
              Puedes tener una impresora en cada lugar. La de <b>caja</b> saca la cuenta
              del cliente; la de <b>cocina</b> saca la comanda para el cocinero en cuanto
              mandas los platillos, sin que nadie tenga que caminar; la de <b>barra</b>
              hace lo mismo con las bebidas. Cada una se configura por separado.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-[#64748B] mb-1">Tipo de conexión</label>
              <select value={printerForm.connection_type} onChange={e => setPrinterForm(f => ({ ...f, connection_type: e.target.value }))}
                className="border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm w-full">
                <option value="browser">Imprimir desde el navegador (USB u otro)</option>
                <option value="network">Impresora de red (IP fija)</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-[#64748B] mb-1">Ancho de papel</label>
              <select value={printerForm.paper_width_mm} onChange={e => setPrinterForm(f => ({ ...f, paper_width_mm: Number(e.target.value) }))}
                className="border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm w-full">
                <option value={80}>80mm (estándar)</option>
                <option value={58}>58mm (compacta)</option>
              </select>
            </div>
            {printerForm.connection_type === 'network' && (
              <>
                <div>
                  <label className="block text-xs text-[#64748B] mb-1">IP de la impresora</label>
                  <input value={printerForm.printer_ip} onChange={e => setPrinterForm(f => ({ ...f, printer_ip: e.target.value }))}
                    placeholder="192.168.1.50" className="border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm w-full" />
                </div>
                <div>
                  <label className="block text-xs text-[#64748B] mb-1">Puerto</label>
                  <input type="number" value={printerForm.printer_port} onChange={e => setPrinterForm(f => ({ ...f, printer_port: e.target.value }))}
                    className="border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm w-full" placeholder="9100" />
                </div>
              </>
            )}
          </div>
          <div className="mt-3">
            <Button onClick={savePrinterConfig} disabled={savingPrinter}>Guardar impresora</Button>
          </div>
        </div>
      )}

      {showTerminalSettings && (
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-4" data-testid="pos-terminal-settings">
          <h2 className="text-sm font-semibold text-[#0F172A] mb-1 flex items-center gap-2"><CreditCard className="w-4 h-4" /> Terminal bancaria (TPV)</h2>
          <p className="text-xs text-[#64748B] mb-3">
            Conecta tu terminal física para mandarle la instrucción de cobro directo desde aquí — el cliente inserta o acerca su tarjeta
            en la terminal y la venta se confirma sola. Necesitas el proveedor ya conectado en Integraciones.
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-[#64748B] mb-1">Proveedor de la terminal</label>
              <select value={terminalForm.provider_key} onChange={e => setTerminalForm(f => ({ ...f, provider_key: e.target.value }))}
                className="border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm w-full">
                <option value="mercadopago">Mercado Pago Point</option>
                <option value="clip">Clip (terminal PinPad)</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-[#64748B] mb-1">
                {terminalForm.provider_key === 'clip' ? 'ID del lector (opcional)' : 'ID de la terminal (device_id)'}
              </label>
              <input value={terminalForm.device_id} onChange={e => setTerminalForm(f => ({ ...f, device_id: e.target.value }))}
                placeholder={terminalForm.provider_key === 'clip' ? 'Déjalo vacío si tienes un solo lector' : 'PAX_A910__SMARTPOS1234567890'}
                className="border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm w-full" />
            </div>
          </div>
          <p className="text-[11px] text-[#94A3B8] mt-2">
            {terminalForm.provider_key === 'clip'
              ? 'Clip: conecta tu API Key de Clip en Integraciones. El cobro se manda a tu lector Clip PinPad registrado; si tienes varios, pon el ID del lector.'
              : 'Mercado Pago: el ID del dispositivo está en tu cuenta de Mercado Pago, sección Point.'}
          </p>
          <label className="flex items-center gap-2 text-sm text-[#475569] mt-3">
            <input type="checkbox" checked={terminalForm.enabled} onChange={e => setTerminalForm(f => ({ ...f, enabled: e.target.checked }))} />
            Terminal activa
          </label>
          <div className="mt-3">
            <Button onClick={saveTerminalConfig} disabled={savingTerminal}>Guardar terminal</Button>
          </div>
        </div>
      )}

      <SectionHelp module="pos" title="el Punto de Venta"
        description="Abre la caja al inicio del turno con el efectivo que tienes. Cada venta en efectivo se cobra al instante y descuenta el inventario si el producto lo lleva; puedes aplicar descuentos y dividir el pago entre varios métodos. Si ligas la venta a un cliente del CRM que tiene una membresía, verás su descuento o créditos disponibles. Para cobrar a distancia, elige un cliente y el sistema le manda un link de pago real de Mercado Pago o Stripe por WhatsApp. También puedes cobrar directo en tu terminal física si la tienes configurada."
        whatItDoesNot="Un cajero no puede reembolsar ventas ni aplicar descuentos grandes por su cuenta — siempre pide el PIN de un gerente. Si reembolsas una venta pagada con tarjeta o link, el dinero tampoco se regresa solo — hay que procesarlo en el panel de tu proveedor de pagos." />

      {/* Caja */}
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-4" data-testid="pos-caja-panel">
        <h2 className="text-sm font-semibold text-[#0F172A] mb-3 flex items-center gap-2">
          {session ? <Unlock className="w-4 h-4 text-emerald-600" /> : <Lock className="w-4 h-4 text-slate-400" />} Caja
        </h2>
        {session ? (
          <div className="flex flex-wrap items-end gap-3">
            <div className="text-sm text-[#475569]">
              Abierta con <strong>{money(session.opening_amount)}</strong>
              {session.opened_at ? ` — ${new Date(session.opened_at).toLocaleString('es-MX')}` : ''}
            </div>
            <div className="flex items-end gap-2 ml-auto">
              <div>
                <label className="block text-xs text-[#64748B] mb-1">Efectivo contado al cerrar</label>
                <input type="number" step="0.01" value={closingCounted} onChange={e => setClosingCounted(e.target.value)}
                  className="border border-[#E2E8F0] rounded-lg px-3 py-1.5 text-sm w-40" placeholder="0.00" />
              </div>
              <Button onClick={closeCaja} variant="outline">Cerrar caja</Button>
            </div>
          </div>
        ) : (
          <div className="flex items-end gap-2">
            <div>
              <label className="block text-xs text-[#64748B] mb-1">Efectivo inicial</label>
              <input type="number" step="0.01" value={openingAmount} onChange={e => setOpeningAmount(e.target.value)}
                className="border border-[#E2E8F0] rounded-lg px-3 py-1.5 text-sm w-40" placeholder="0.00" />
            </div>
            <Button onClick={openCaja}>Abrir caja</Button>
          </div>
        )}
        {closingResult && (
          <div className="mt-3 text-sm bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-3 flex items-center justify-between flex-wrap gap-2">
            <div>
              Esperado: <strong>{money(closingResult.closing_amount_expected)}</strong> · Contado: <strong>{money(closingResult.closing_amount_counted)}</strong> ·
              {' '}Diferencia:{' '}
              <strong className={closingResult.difference < 0 ? 'text-red-600' : closingResult.difference > 0 ? 'text-emerald-600' : ''}>
                {money(closingResult.difference)}
              </strong>
            </div>
            <Button size="sm" variant="outline" onClick={downloadArqueo} data-testid="pos-download-arqueo">Descargar arqueo</Button>
          </div>
        )}
      </div>

      {/* Nueva venta */}
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-4" data-testid="pos-new-sale-panel">
        <h2 className="text-sm font-semibold text-[#0F172A] mb-3 flex items-center gap-2"><ShoppingCart className="w-4 h-4" /> Nueva venta</h2>
        {terminalWaiting ? (
          <div className="flex flex-col items-center justify-center py-10 gap-3" data-testid="pos-terminal-waiting">
            <Loader2 className="w-10 h-10 text-[#0F172A] animate-spin" />
            <p className="text-sm font-medium text-[#0F172A]">Esperando el pago en la terminal…</p>
            <p className="text-xs text-[#64748B] text-center max-w-xs">
              Pide al cliente que inserte, acerque o deslice su tarjeta en la terminal física. Esta pantalla se actualiza sola en cuanto la terminal confirme el cobro.
            </p>
            <Button variant="outline" onClick={cancelTerminalWait}>Cancelar</Button>
          </div>
        ) : (
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-[#64748B] mb-1">Agregar del catálogo</label>
            <select onChange={e => { const item = items.find(i => i.id === e.target.value); if (item) addToCart(item); e.target.value = ''; }}
              className="border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm w-full" defaultValue="">
              <option value="" disabled>Elige un producto o servicio…</option>
              {items.map(i => (
                <option key={i.id} value={i.id}>
                  {i.name} — {money(i.price_min)}{i.track_inventory ? ` (stock: ${i.quantity_available})` : ''}
                </option>
              ))}
            </select>

            {cart.length > 0 && (
              <div className="mt-3 space-y-2">
                {/* S56 (1/N) — beneficios de membresía del cliente elegido */}
                {selectedLead && memberships.length > 0 && (
                  <div className="space-y-1.5" data-testid="pos-membership-banner">
                    {memberships.map(m => (
                      <div key={m.membership_id} className="bg-violet-50 border border-violet-200 rounded-lg px-3 py-2 text-xs text-violet-900">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-medium">
                            {m.tier_label ? `Cliente ${m.tier_label}` : 'Cliente con membresía'}
                            {m.discount_percent ? ` — ${m.discount_percent}% de descuento disponible` : ''}
                          </span>
                          {m.discount_percent > 0 && (
                            <button type="button"
                              onClick={() => { setDiscountType('percentage'); setDiscountValue(String(m.discount_percent)); }}
                              className="text-violet-700 underline hover:text-violet-900 whitespace-nowrap">
                              Aplicar descuento
                            </button>
                          )}
                        </div>
                        {m.credits_remaining > 0 && <p className="mt-0.5">{m.credits_remaining} crédito(s) disponibles en esta membresía.</p>}
                      </div>
                    ))}
                  </div>
                )}

                {cart.map(l => {
                  const applicableMembership = memberships.find(m =>
                    m.credits_remaining > 0 && (m.discount_scope === 'all' || m.discount_category === l.category)
                  );
                  return (
                    <div key={l.catalog_item_id} className="text-sm">
                      <div className="flex items-center gap-2">
                        <span className="flex-1">{l.name}</span>
                        <input type="number" min="1" value={l.quantity} onChange={e => updateQty(l.catalog_item_id, Number(e.target.value))}
                          className="w-16 border border-[#E2E8F0] rounded px-2 py-1 text-center" />
                        <span className="w-20 text-right">{l.use_membership_credit ? money(0) : money(l.price_min * l.quantity)}</span>
                        <button onClick={() => removeFromCart(l.catalog_item_id)} className="text-slate-400 hover:text-red-600">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      {applicableMembership && l.quantity === 1 && (
                        <label className="flex items-center gap-1.5 text-[11px] text-violet-700 mt-0.5 ml-0.5 cursor-pointer">
                          <input type="checkbox" checked={!!l.use_membership_credit}
                            onChange={e => setCart(prev => prev.map(row => row.catalog_item_id === l.catalog_item_id
                              ? { ...row, use_membership_credit: e.target.checked, membership_id: e.target.checked ? applicableMembership.membership_id : null }
                              : row))} />
                          Usar 1 crédito de membresía (queda gratis)
                        </label>
                      )}
                    </div>
                  );
                })}

                {/* S55 (1/N) — descuento sobre el total del ticket */}
                <div className="flex items-center gap-2 pt-2 border-t border-[#E2E8F0]">
                  <select value={discountType} onChange={e => setDiscountType(e.target.value)}
                    className="border border-[#E2E8F0] rounded-lg px-2 py-1.5 text-xs">
                    <option value="none">Sin descuento</option>
                    <option value="percentage">Descuento %</option>
                    <option value="fixed">Descuento $</option>
                  </select>
                  {discountType !== 'none' && (
                    <input type="number" min="0" step="0.01" value={discountValue}
                      onChange={e => setDiscountValue(e.target.value)}
                      placeholder={discountType === 'percentage' ? '10' : '50.00'}
                      className="border border-[#E2E8F0] rounded-lg px-2 py-1.5 text-xs w-24" />
                  )}
                </div>

                <div className="space-y-1 pt-1 text-sm">
                  <div className="flex justify-between text-[#64748B]"><span>Subtotal</span><span>{money(cartTotal)}</span></div>
                  {discountAmount > 0 && (
                    <div className="flex justify-between text-emerald-700"><span>Descuento</span><span>-{money(discountAmount)}</span></div>
                  )}
                  {taxAmount > 0 && (
                    <div className="flex justify-between text-[#64748B]"><span>IVA ({taxRate}%{pricesIncludeTax ? ', incluido' : ''})</span><span>{money(taxAmount)}</span></div>
                  )}
                  <div className="flex justify-between font-semibold text-[#0F172A] pt-1 border-t border-[#E2E8F0]">
                    <span>Total</span><span>{money(grandTotal)}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div>
            <div className="flex gap-2 mb-3">
              <button onClick={() => setSaleType('cash')}
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border ${saleType === 'cash' ? 'bg-[#0F172A] text-white border-[#0F172A]' : 'border-[#E2E8F0] text-[#64748B]'}`}>
                Efectivo
              </button>
              <button onClick={() => setSaleType('remote')}
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border ${saleType === 'remote' ? 'bg-[#0F172A] text-white border-[#0F172A]' : 'border-[#E2E8F0] text-[#64748B]'}`}>
                Link por WhatsApp
              </button>
              <button onClick={() => setSaleType('terminal')}
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border flex items-center justify-center gap-1 ${saleType === 'terminal' ? 'bg-[#0F172A] text-white border-[#0F172A]' : 'border-[#E2E8F0] text-[#64748B]'}`}>
                <CreditCard className="w-3.5 h-3.5" /> Terminal
              </button>
            </div>

            {/* S56 (1/N) — el selector de cliente ya no es exclusivo del
                link por WhatsApp: ligar un cliente real a una venta en
                efectivo o terminal permite aplicar sus beneficios de
                membresía. Sigue siendo opcional salvo para el link (ver
                validación en submitRemoteSale). */}
            <div className="mb-3">
              <label className="block text-xs text-[#64748B] mb-1">
                Cliente (CRM){saleType !== 'remote' ? ' — opcional' : ''}
              </label>
              {selectedLead ? (
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm">
                    <span>{selectedLead.display_name}{selectedLead.phone ? ` — ${selectedLead.phone}` : ''}</span>
                    <button onClick={() => { setSelectedLead(null); setLeadSearch(''); setCrmDealId(''); setDealVehicle(null); }} className="text-xs text-[#64748B] hover:text-red-600">Cambiar</button>
                  </div>
                  {dealVehicle && (
                    <div className="flex items-center gap-2 border border-[#E2E8F0] bg-[#F8FAFC] rounded-lg px-3 py-2 text-xs text-[#334155]" data-testid="pos-vehicle-banner">
                      <Package className="w-3.5 h-3.5 text-[#64748B]" />
                      <span>
                        Vehículo: {[dealVehicle.brand, dealVehicle.model, dealVehicle.year].filter(Boolean).join(' ') || 'sin datos'}
                        {dealVehicle.license_plate ? ` · Placa ${dealVehicle.license_plate}` : ''}
                      </span>
                    </div>
                  )}
                </div>
              ) : leadFromUrlLoading ? (
                <div className="flex items-center gap-2 text-xs text-[#64748B] px-1 py-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Cargando cliente…
                </div>
              ) : showNewLeadForm ? (
                <div className="border border-[#E2E8F0] rounded-lg p-2 space-y-2" data-testid="pos-new-lead-form">
                  <input value={newLeadName} onChange={e => setNewLeadName(e.target.value)}
                    placeholder="Nombre del cliente nuevo…" autoFocus
                    className="border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm w-full" />
                  <div className="flex gap-2">
                    <button onClick={createNewLead} disabled={creatingLead}
                      className="flex-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-[#0F172A] text-white disabled:opacity-50">
                      {creatingLead ? 'Creando…' : 'Crear y usar'}
                    </button>
                    <button onClick={() => { setShowNewLeadForm(false); setNewLeadName(''); }}
                      className="px-3 py-1.5 rounded-lg text-xs font-medium border border-[#E2E8F0] text-[#64748B]">
                      Cancelar
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex gap-2">
                    <input value={leadSearch} onChange={e => setLeadSearch(e.target.value)} placeholder="Buscar por nombre o teléfono…"
                      className="border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm w-full" />
                    <button onClick={() => setShowNewLeadForm(true)} title="Cliente nuevo (venta mostrador)"
                      className="flex-shrink-0 px-3 py-2 rounded-lg text-xs font-medium border border-[#E2E8F0] text-[#64748B] hover:bg-[#F8FAFC] flex items-center gap-1">
                      <UserPlus className="w-3.5 h-3.5" /> Nuevo
                    </button>
                  </div>
                  {leadOptions.length > 0 && (
                    <div className="border border-[#E2E8F0] rounded-lg mt-1 max-h-40 overflow-y-auto">
                      {leadOptions.map(l => (
                        <button key={l.id} onClick={() => { setSelectedLead(l); setLeadOptions([]); setCrmDealId(''); setDealVehicle(null); }}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-[#F8FAFC] border-b border-[#F1F5F9] last:border-0">
                          {l.display_name}{l.phone ? ` — ${l.phone}` : ' (sin teléfono)'}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            {saleType === 'cash' && (
              <div className="mb-3">
                <label className="flex items-center gap-2 text-xs text-[#475569] mb-2">
                  <input type="checkbox" checked={splitPayment}
                    onChange={e => { setSplitPayment(e.target.checked); setAmountTendered(''); }} />
                  Dividir el pago entre varios métodos (ej. mitad efectivo, mitad tarjeta)
                </label>

                {splitPayment ? (
                  <div className="border border-[#E2E8F0] rounded-xl p-3 bg-[#F8FAFC]" data-testid="pos-split-payment">
                    <div className="space-y-2">
                      {paymentLines.map((p, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <select value={p.payment_method}
                            onChange={e => setPaymentLines(prev => prev.map((row, i) => i === idx ? { ...row, payment_method: e.target.value } : row))}
                            className="border border-[#E2E8F0] rounded-lg px-2 py-1.5 text-xs flex-1">
                            <option value="cash">Efectivo</option>
                            <option value="card_provider">Tarjeta</option>
                            <option value="transfer">Transferencia</option>
                          </select>
                          <input type="number" step="0.01" min="0" value={p.amount}
                            onChange={e => setPaymentLines(prev => prev.map((row, i) => i === idx ? { ...row, amount: e.target.value } : row))}
                            placeholder="0.00" className="border border-[#E2E8F0] rounded-lg px-2 py-1.5 text-sm w-28 text-right" />
                          {paymentLines.length > 1 && (
                            <button onClick={() => setPaymentLines(prev => prev.filter((_, i) => i !== idx))} className="text-slate-400 hover:text-red-600">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                    <button type="button" onClick={() => setPaymentLines(prev => [...prev, { payment_method: 'cash', amount: '' }])}
                      className="text-xs text-blue-600 hover:underline mt-2">+ Agregar otro método</button>
                    <div className={`mt-2 text-xs font-medium ${paymentLinesMatch ? 'text-emerald-700' : 'text-red-600'}`}>
                      Suma: {money(paymentLinesSum)} de {money(grandTotal)} {paymentLinesMatch ? '✓' : ''}
                    </div>
                  </div>
                ) : (
                  <div className="border border-[#E2E8F0] rounded-xl p-3 bg-[#F8FAFC]" data-testid="pos-cash-calculator">
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-xs font-semibold text-[#0F172A]">Calculadora de cambio</label>
                      <span className="text-xs text-[#64748B]">Total: <strong className="text-[#0F172A]">{money(grandTotal)}</strong></span>
                    </div>
                    <div className="text-xs text-[#64748B] mb-1">¿Con cuánto paga el cliente?</div>
                    <input type="number" step="0.01" min="0" value={amountTendered}
                      onChange={e => setAmountTendered(e.target.value)}
                      placeholder="0.00"
                      className="border border-[#E2E8F0] rounded-lg px-3 py-2 text-lg font-semibold text-center w-full mb-2" data-testid="pos-amount-tendered" />
                    {/* Toca los billetes que te da el cliente — se van sumando, como en la caja del súper */}
                    <div className="grid grid-cols-4 gap-1.5 mb-2">
                      {[20, 50, 100, 200, 500, 1000].map(bill => (
                        <button key={bill} type="button"
                          onClick={() => setAmountTendered(prev => String(Math.round(((Number(prev) || 0) + bill) * 100) / 100))}
                          className="px-2 py-2 rounded-lg text-sm font-medium border border-[#E2E8F0] bg-white hover:bg-[#F1F5F9] text-[#334155]">
                          +${bill}
                        </button>
                      ))}
                      <button type="button" onClick={() => setAmountTendered(String(grandTotal))}
                        className="px-2 py-2 rounded-lg text-sm font-medium border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-emerald-700">
                        Exacto
                      </button>
                      <button type="button" onClick={() => setAmountTendered('')}
                        className="px-2 py-2 rounded-lg text-sm font-medium border border-[#E2E8F0] bg-white hover:bg-[#F1F5F9] text-[#64748B]">
                        Limpiar
                      </button>
                    </div>
                    {amountTendered !== '' && (
                      Number(amountTendered) >= grandTotal ? (
                        <div className="bg-emerald-600 rounded-lg px-3 py-3 text-center" data-testid="pos-live-change">
                          <p className="text-xs text-emerald-50">Cambio a entregar</p>
                          <p className="text-3xl font-extrabold text-white leading-tight">{money(Number(amountTendered) - grandTotal)}</p>
                        </div>
                      ) : (
                        <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-center">
                          <p className="text-sm font-semibold text-red-600">Falta {money(grandTotal - Number(amountTendered))}</p>
                          <p className="text-xs text-red-500">El efectivo recibido es menor al total</p>
                        </div>
                      )
                    )}
                  </div>
                )}
              </div>
            )}

            <label className="block text-xs text-[#64748B] mb-1">Notas (opcional)</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
              className="border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm w-full mb-3" />

            {saleType === 'cash' ? (
              <Button onClick={submitCashSale}
                disabled={submitting || !session || !cart.length ||
                  (!splitPayment && amountTendered !== '' && Number(amountTendered) < grandTotal) ||
                  (splitPayment && !paymentLinesMatch)}
                className="w-full">
                {session ? `Cobrar ${money(grandTotal)}` : 'Abre la caja primero'}
              </Button>
            ) : saleType === 'terminal' ? (
              <Button onClick={submitTerminalSale}
                disabled={submitting || !cart.length || !terminalConfig || (terminalConfig.provider_key === 'mercadopago' && !terminalConfig.device_id)}
                className="w-full flex items-center justify-center gap-2">
                <CreditCard className="w-4 h-4" /> Cobrar {money(grandTotal)} con terminal
              </Button>
            ) : (
              <Button onClick={submitRemoteSale} disabled={submitting || !cart.length} className="w-full flex items-center justify-center gap-2">
                <Send className="w-4 h-4" /> Enviar link de pago por WhatsApp
              </Button>
            )}
            {saleType === 'terminal' && (!terminalConfig || (terminalConfig.provider_key === 'mercadopago' && !terminalConfig.device_id)) && (
              <p className="mt-2 text-xs text-amber-700">Configura tu terminal bancaria arriba antes de cobrar con ella.</p>
            )}

            {lastRemoteResult && (
              <div className="mt-3 text-xs bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-3 break-all">
                Link generado:{' '}
                <a href={lastRemoteResult.checkout_url} target="_blank" rel="noreferrer" className="text-blue-600 underline">
                  {lastRemoteResult.checkout_url}
                </a>
                {!lastRemoteResult.whatsapp_sent && <p className="text-amber-700 mt-1">No se pudo enviar por WhatsApp — cópialo y mándalo a mano.</p>}
              </div>
            )}

            {lastChangeResult && (
              <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-3 text-center" data-testid="pos-last-change">
                <p className="text-xs text-emerald-700">Venta cobrada — recibiste {money(lastChangeResult.amount_tendered)}</p>
                <p className="text-2xl font-bold text-emerald-700">Cambio: {money(lastChangeResult.change_due)}</p>
              </div>
            )}
          </div>
        </div>
        )}
      </div>

      {/* Sprint 53 (2/N) — antes un cobro pendiente (ej. un depósito de
          Agenda esperando pago) se veía idéntico a cualquier otra venta,
          mezclado en la misma tabla con un badge ámbar genérico y sin
          descripción — un cajero no tenía forma de notar que existía. */}
      {sales.some(s => s.status === 'pending') && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="pos-pending-charges">
          <h2 className="text-sm font-semibold text-amber-900 mb-3">Cobros pendientes de pago</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-amber-800 border-b border-amber-200">
                  <th className="py-2 pr-3">Folio</th><th className="pr-3">Fecha</th><th className="pr-3">Descripción</th><th className="pr-3">Origen</th>
                  <th className="pr-3">Total</th><th></th>
                </tr>
              </thead>
              <tbody>
                {sales.filter(s => s.status === 'pending').map(s => (
                  <tr key={s.id} className="border-b border-amber-100">
                    <td className="py-2 pr-3 font-mono text-amber-700">{s.display_code || '—'}</td>
                    <td className="py-2 pr-3 text-amber-900">{s.created_at ? new Date(s.created_at).toLocaleString('es-MX') : '—'}</td>
                    <td className="pr-3 text-amber-900">{s.notes || '—'}</td>
                    <td className="pr-3 text-amber-900">{s.sale_type === 'remote' ? 'Link de pago remoto' : 'Presencial'}</td>
                    <td className="pr-3 font-medium text-amber-900">{money(s.total, s.currency)}</td>
                    <td className="text-right whitespace-nowrap">
                      <button onClick={() => cancelSale(s.id)} className="text-xs text-amber-700 hover:text-red-600">Cancelar</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Historial */}
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-4" data-testid="pos-sales-history">
        <h2 className="text-sm font-semibold text-[#0F172A] mb-3 flex items-center gap-2"><History className="w-4 h-4" /> Historial de ventas</h2>
        {sales.length === 0 ? (
          <p className="text-sm text-[#94A3B8]">Todavía no hay ventas registradas.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-[#64748B] border-b border-[#E2E8F0]">
                  <th className="py-2 pr-3">Folio</th><th className="pr-3">Fecha</th><th className="pr-3">Descripción</th><th className="pr-3">Tipo</th><th className="pr-3">Método</th>
                  <th className="pr-3">Total</th><th className="pr-3">Estado</th><th></th>
                </tr>
              </thead>
              <tbody>
                {sales.map(s => (
                  <tr key={s.id} className="border-b border-[#F1F5F9]">
                    <td className="py-2 pr-3 font-mono text-[#94A3B8]" title="Código de referencia de esta venta">{s.display_code || '—'}</td>
                    <td className="py-2 pr-3 text-[#475569]">{s.created_at ? new Date(s.created_at).toLocaleString('es-MX') : '—'}</td>
                    <td className="pr-3 text-[#475569] max-w-[220px] truncate" title={s.notes || ''}>{s.notes || '—'}</td>
                    <td className="pr-3">{s.sale_type === 'remote' ? 'Link WhatsApp' : 'Presencial'}</td>
                    <td className="pr-3">
                      {s.payment_method === 'cash' ? 'Efectivo'
                        : s.payment_method === 'split'
                          ? `Dividido (${(s.payments || []).map(p => p.payment_method === 'cash' ? 'efectivo' : p.payment_method === 'card_provider' ? 'tarjeta' : p.payment_method).join(' + ')})`
                          : (s.payment_provider_key || '—')}
                    </td>
                    <td className="pr-3 font-medium">
                      {money(s.total, s.currency)}
                      {s.discount_amount > 0 && <span className="block text-[10px] text-emerald-700">-{money(s.discount_amount, s.currency)} desc.</span>}
                    </td>
                    <td className="pr-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[s.status] || ''}`}>
                        {STATUS_LABELS[s.status] || s.status}
                      </span>
                    </td>
                    <td className="text-right whitespace-nowrap">
                      {s.status === 'paid' && (
                        <>
                          <button onClick={() => printSale(s.id)} disabled={printingId === s.id}
                            className="text-xs text-[#64748B] hover:text-[#0F172A] inline-flex items-center gap-1 mr-3 disabled:opacity-50">
                            <Printer className="w-3.5 h-3.5" /> {printingId === s.id ? 'Imprimiendo…' : 'Ticket'}
                          </button>
                          <button onClick={() => refundSale(s.id)} disabled={refundingId === s.id}
                            className="text-xs text-[#64748B] hover:text-red-600 disabled:opacity-50">
                            {refundingId === s.id ? 'Reembolsando…' : 'Reembolsar'}
                          </button>
                        </>
                      )}
                      {s.status === 'pending' && (
                        <button onClick={() => cancelSale(s.id)} className="text-xs text-[#64748B] hover:text-red-600">Cancelar</button>
                      )}
                      {s.status === 'refunded' && s.refund_reason && (
                        <span className="text-xs text-[#94A3B8]" title={s.refund_reason}>reembolsada</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* S56 (1/N) — PIN de supervisor: aparece cuando el backend rechaza
          un reembolso o un descuento grande hecho por un cajero. */}
      {pinModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => { setPinModal(null); setPinInput(''); }}>
          <div className="bg-white rounded-xl border shadow-xl max-w-sm w-full mx-4 p-5" onClick={e => e.stopPropagation()} data-testid="supervisor-pin-modal">
            <h3 className="text-sm font-bold text-[#0F172A] mb-2">Autorización de gerente requerida</h3>
            <p className="text-xs text-[#64748B] mb-3">{pinModal.message}</p>
            <input type="password" inputMode="numeric" maxLength={6} autoFocus value={pinInput}
              onChange={e => setPinInput(e.target.value.replace(/\D/g, ''))}
              placeholder="PIN del gerente"
              className="w-full px-3 py-2 text-lg text-center tracking-widest border border-[#E2E8F0] rounded-lg mb-3"
              data-testid="supervisor-pin-input" />
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => { setPinModal(null); setPinInput(''); }}>Cancelar</Button>
              <Button className="flex-1" disabled={pinSubmitting || pinInput.length < 4}
                onClick={async () => { setPinSubmitting(true); try { await pinModal.onConfirm(pinInput); } finally { setPinSubmitting(false); } }}
                data-testid="supervisor-pin-confirm">
                {pinSubmitting ? 'Verificando…' : 'Autorizar'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
